import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Switch, FormControlLabel,
  Select, MenuItem, IconButton, Divider, CircularProgress, Alert, Chip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadIcon from '@mui/icons-material/Upload';
import { adminFetch } from '../../lib/adminAuth';

const API = process.env.REACT_APP_API_URL || '';

const NEW_BLOCK = {
  heading: { type: 'heading', level: 2, text: '' },
  paragraph: { type: 'paragraph', text: '' },
  image: { type: 'image', url: '', caption: '', alt: '', alignment: 'full' },
  quote: { type: 'quote', text: '', attribution: '' },
  list: { type: 'list', ordered: false, items: [''] },
  divider: { type: 'divider' },
  'two-column': { type: 'two-column', leftText: '', rightText: '' },
};

function slugify(s) {
  return String(s || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/* ─── Image Upload helper ─── */
function ImageUploadField({ value, onChange, label = 'Image' }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await adminFetch(`${API}/api/admin/blogs/upload-image`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.url) {
        onChange(data.url);
      } else {
        setError(data?.message || 'Upload failed. Please try again.');
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5 }}>{label}</Typography>
      {value && (
        <Box component="img" src={value} alt="" sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1.5, mb: 1, display: 'block' }} />
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Image URL or upload below"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <input ref={ref} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        <Button size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />} onClick={() => ref.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
        {value && (
          <Button size="small" color="error" onClick={() => onChange('')} sx={{ textTransform: 'none' }}>
            Remove
          </Button>
        )}
      </Box>
      {error && <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

/* ─── Block Editor ─── */
function BlockEditor({ block, index, total, onChange, onMove, onDelete }) {
  const update = (field, value) => onChange(index, { ...block, [field]: value });

  const updateItem = (i, value) => {
    const items = [...(block.items || [])];
    items[i] = value;
    onChange(index, { ...block, items });
  };
  const addItem = () => onChange(index, { ...block, items: [...(block.items || []), ''] });
  const removeItem = (i) => {
    const items = [...(block.items || [])];
    items.splice(i, 1);
    onChange(index, { ...block, items });
  };

  const labels = {
    heading: 'Heading', paragraph: 'Paragraph', image: 'Image',
    quote: 'Quote', list: 'List', divider: 'Divider', 'two-column': 'Two Columns',
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e8e8e8', borderRadius: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip label={`#${index + 1}`} size="small" sx={{ fontSize: 11, bgcolor: '#f5f5f5' }} />
        <Chip label={labels[block.type] || block.type} size="small" sx={{ fontSize: 11, bgcolor: '#fff8e7', color: '#b07800', fontWeight: 600 }} />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.3 }}>
          <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, -1)}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(index, 1)}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" onClick={() => onDelete(index)}><DeleteIcon sx={{ fontSize: 16, color: '#c62828' }} /></IconButton>
        </Box>
      </Box>

      {block.type === 'heading' && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#888' }}>Level:</Typography>
            <ToggleButtonGroup size="small" exclusive value={block.level || 2} onChange={(_, v) => v && update('level', v)}>
              <ToggleButton value={2} sx={{ textTransform: 'none', px: 2 }}>H2</ToggleButton>
              <ToggleButton value={3} sx={{ textTransform: 'none', px: 2 }}>H3</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField fullWidth size="small" placeholder="Heading text" value={block.text || ''} onChange={(e) => update('text', e.target.value)} />
        </>
      )}

      {block.type === 'paragraph' && (
        <>
          <TextField fullWidth size="small" multiline rows={4} placeholder="Paragraph text. Use **bold**, *italic*, [link](https://...)" value={block.text || ''} onChange={(e) => update('text', e.target.value)} />
          <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>
            Inline formatting: <code>**bold**</code> · <code>*italic*</code> · <code>[text](url)</code>
          </Typography>
        </>
      )}

      {block.type === 'image' && (
        <>
          <ImageUploadField value={block.url} onChange={(v) => update('url', v)} label="Image URL" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }}>
            <TextField size="small" label="Caption" value={block.caption || ''} onChange={(e) => update('caption', e.target.value)} />
            <TextField size="small" label="Alt text" value={block.alt || ''} onChange={(e) => update('alt', e.target.value)} />
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#888' }}>Alignment:</Typography>
            <ToggleButtonGroup size="small" exclusive value={block.alignment || 'full'} onChange={(_, v) => v && update('alignment', v)}>
              <ToggleButton value="left" sx={{ textTransform: 'none', px: 1.5 }}>Left</ToggleButton>
              <ToggleButton value="center" sx={{ textTransform: 'none', px: 1.5 }}>Center</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', px: 1.5 }}>Right</ToggleButton>
              <ToggleButton value="full" sx={{ textTransform: 'none', px: 1.5 }}>Full</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </>
      )}

      {block.type === 'quote' && (
        <>
          <TextField fullWidth size="small" multiline rows={3} placeholder="Quote text" value={block.text || ''} onChange={(e) => update('text', e.target.value)} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" placeholder="Attribution (optional)" value={block.attribution || ''} onChange={(e) => update('attribution', e.target.value)} />
        </>
      )}

      {block.type === 'list' && (
        <>
          <FormControlLabel
            control={<Switch checked={!!block.ordered} onChange={(e) => update('ordered', e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: 13 }}>Ordered (1, 2, 3…)</Typography>}
            sx={{ mb: 1 }}
          />
          {(block.items || []).map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <Typography sx={{ fontSize: 12, color: '#999', minWidth: 16 }}>{block.ordered ? `${i + 1}.` : '•'}</Typography>
              <TextField size="small" fullWidth value={item} onChange={(e) => updateItem(i, e.target.value)} placeholder="List item" />
              <IconButton size="small" onClick={() => removeItem(i)}><DeleteIcon sx={{ fontSize: 16, color: '#999' }} /></IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ textTransform: 'none', fontSize: 12 }}>Add item</Button>
        </>
      )}

      {block.type === 'divider' && (
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box sx={{ borderTop: '1px solid #ddd', mx: 4 }} />
          <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.5 }}>Horizontal divider</Typography>
        </Box>
      )}

      {block.type === 'two-column' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField size="small" label="Left column" multiline rows={4} value={block.leftText || ''} onChange={(e) => update('leftText', e.target.value)} />
          <TextField size="small" label="Right column" multiline rows={4} value={block.rightText || ''} onChange={(e) => update('rightText', e.target.value)} />
        </Box>
      )}
    </Paper>
  );
}

/* ─── Main editor ─── */
export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [author, setAuthor] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [published, setPublished] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const res = await adminFetch(`${API}/api/admin/blogs/${id}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        const p = data.post;
        setTitle(p.title || '');
        setSlug(p.slug || '');
        setExcerpt(p.excerpt || '');
        setCoverImage(p.coverImage || '');
        setAuthor(p.author || '');
        setBlocks(p.blocks || []);
        setPublished(p.published || false);
        setSlugTouched(true);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  // Auto-generate slug from title until user manually edits it
  useEffect(() => {
    if (!slugTouched && title) setSlug(slugify(title));
  }, [title, slugTouched]);

  const updateBlock = (i, updated) => {
    const arr = [...blocks];
    arr[i] = updated;
    setBlocks(arr);
  };
  const moveBlock = (i, dir) => {
    const arr = [...blocks];
    const j = i + dir;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBlocks(arr);
  };
  const deleteBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const addBlock = (type) => setBlocks([...blocks, { ...NEW_BLOCK[type] }]);

  const handleSave = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const body = { title, slug, excerpt, coverImage, author, blocks, published };
      const url = isNew ? `${API}/api/admin/blogs` : `${API}/api/admin/blogs/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Saved successfully');
      if (isNew) navigate(`/admin/blogs/${data.post._id}`, { replace: true });
      else { setSlug(data.post.slug); }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/blogs')} size="small"><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{isNew ? 'New Blog Post' : 'Edit Blog Post'}</Typography>
          {!isNew && slug && <Typography variant="caption" sx={{ color: '#999' }}>/blogs/{slug}</Typography>}
        </Box>
        <Button startIcon={<VisibilityIcon />} onClick={() => setShowPreview(!showPreview)} sx={{ textTransform: 'none', color: showPreview ? '#1a1a2e' : '#999' }}>
          {showPreview ? 'Hide Preview' : 'Preview'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Meta */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e8e8e8', borderRadius: 2, mb: 2 }}>
            <TextField fullWidth size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Slug" value={slug} onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }} helperText="URL: /blogs/{slug}" sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} multiline rows={2} inputProps={{ maxLength: 500 }} helperText={`${excerpt.length} / 500 — shown on the listing page`} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Author" value={author} onChange={(e) => setAuthor(e.target.value)} sx={{ mb: 2 }} />
            <ImageUploadField value={coverImage} onChange={setCoverImage} label="Cover Image" />
          </Paper>

          {/* Blocks */}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Content Blocks ({blocks.length})
          </Typography>

          {blocks.map((block, i) => (
            <BlockEditor key={i} block={block} index={i} total={blocks.length} onChange={updateBlock} onMove={moveBlock} onDelete={deleteBlock} />
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {Object.keys(NEW_BLOCK).map(type => (
              <Button
                key={type}
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addBlock(type)}
                sx={{ textTransform: 'none', fontSize: 12, color: '#555', border: '1px solid #e0e0e0', borderRadius: 1.5 }}
              >
                {type === 'two-column' ? 'Two Columns' : type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={published} onChange={(e) => setPublished(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Published</Typography>}
            />
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !title.trim()}
                sx={{ bgcolor: '#1a1a2e', color: '#fff', textTransform: 'none', borderRadius: 2, px: 3, '&:hover': { bgcolor: '#2a2a4e' } }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Preview pane */}
        {showPreview && (
          <Box sx={{ width: 480, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, color: '#999', mb: 1.5, textAlign: 'center' }}>Preview</Typography>
            <PreviewPane title={title} excerpt={excerpt} coverImage={coverImage} author={author} blocks={blocks} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ─── Preview pane (mirrors public BlogPost rendering, scaled down) ─── */
function PreviewPane({ title, excerpt, coverImage, author, blocks }) {
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e8e8e8', borderRadius: 3, p: 3, maxHeight: '80vh', overflow: 'auto' }}>
      {coverImage && <Box component="img" src={coverImage} alt="" sx={{ width: '100%', borderRadius: 2, mb: 2 }} />}
      <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#111', mb: 1.5, lineHeight: 1.2 }}>{title || 'Untitled'}</Typography>
      {author && <Typography sx={{ fontSize: 12, color: '#999', mb: 2 }}>By {author}</Typography>}
      {excerpt && <Typography sx={{ color: '#666', fontSize: '1rem', lineHeight: 1.6, mb: 3 }}>{excerpt}</Typography>}
      {(blocks || []).map((b, i) => <PreviewBlock key={i} block={b} />)}
    </Box>
  );
}

function PreviewBlock({ block }) {
  switch (block.type) {
    case 'heading':
      return block.level === 3
        ? <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#222', mt: 2, mb: 1 }}>{block.text}</Typography>
        : <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#111', mt: 3, mb: 1 }}>{block.text}</Typography>;
    case 'paragraph':
      return <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.75, color: '#333', mb: 1.5, whiteSpace: 'pre-wrap' }}>{block.text}</Typography>;
    case 'image':
      return block.url ? <Box component="img" src={block.url} alt={block.alt || ''} sx={{ width: '100%', borderRadius: 1.5, my: 2 }} /> : null;
    case 'quote':
      return (
        <Box sx={{ borderLeft: '3px solid #ffb71b', pl: 2, my: 2, color: '#444', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
          {block.text}
          {block.attribution && <Typography sx={{ fontSize: 12, color: '#999', mt: 0.5, fontStyle: 'normal' }}>— {block.attribution}</Typography>}
        </Box>
      );
    case 'list':
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Box component={Tag} sx={{ pl: 3, my: 1.5, fontSize: '0.95rem', color: '#333' }}>
          {(block.items || []).map((it, i) => <li key={i}>{it}</li>)}
        </Box>
      );
    case 'divider':
      return <Box sx={{ borderTop: '1px solid #eee', my: 3 }} />;
    case 'two-column':
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, my: 2 }}>
          <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{block.leftText}</Typography>
          <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{block.rightText}</Typography>
        </Box>
      );
    default: return null;
  }
}
